import BaseService from '@core/common/BaseService';
import {
  AgentManagerClient,
  RESOURCES,
} from '@core/clients/AgentManagerClient';
import { SOURCE_CONTEXT_NAMES, WIDGET_LOGICAL_NAMES } from '@config/Consts';
import { Subscription } from '@core/common/Subscription';
import queueHealthTransformer from './queueHealthTransformer';

let cancellationToken = null;

function createQueueHealthClients(params) {
  const currentQueueHealthClient = new AgentManagerClient({
    resource: RESOURCES.METRICS,
    contextName: SOURCE_CONTEXT_NAMES[WIDGET_LOGICAL_NAMES.IN_QUEUE],
  });

  if (params?.interceptor) {
    currentQueueHealthClient.useRequestInterceptor(params.interceptor);
  }

  return {
    currentQueueHealthClient,
  };
}

function stopListenToQueueHealthClientEvents() {
  const queueHealthClient = this.queueHealthClients.currentQueueHealthClient;
  queueHealthClient.clearAllListeners(queueHealthClient.constructor.EVENTS.onPollingSuccess);
  queueHealthClient.clearAllListeners(queueHealthClient.constructor.EVENTS.onPollingError);
}

async function enrichQueueHealthData(settings, currentQueueHealthResponse) {
  const queueHealthResponse = queueHealthTransformer.transformQueueHealthResponse({
    currentQueueHealthResponse,
  });

  this.subscription.emit(Subscription.EVENTS.onSubscriptionUpdateNotification, queueHealthResponse);
}

function onQueueHealthSubscriptionError(error) {
  this.subscription.emit(Subscription.EVENTS.onSubscriptionError, error);
}

function listenToQueueHealthClientEvents(settings) {
  const queueHealthClient = this.queueHealthClients.currentQueueHealthClient;
  const clientEvents = queueHealthClient.constructor.EVENTS;
  stopListenToQueueHealthClientEvents.call(this);
  queueHealthClient.on(
    clientEvents.onPollingSuccess,
    enrichQueueHealthData.bind(this, settings),
    'InQueueServicePollingSuccess');
  queueHealthClient.on(
    clientEvents.onPollingError, onQueueHealthSubscriptionError.bind(this),
    'InQueueServicePollingError');
}

function queueHealthSubscriptionStartMethod(settings) {
  const queueHealthRequest = queueHealthTransformer.transformQueueHealthRequest(settings);

  listenToQueueHealthClientEvents.call(this, settings);
  cancellationToken =
    this.queueHealthClients.currentQueueHealthClient.subscribe(queueHealthRequest);
}

function queueHealthSubscriptionStopMethod() {
  if (cancellationToken) {
    stopListenToQueueHealthClientEvents.call(this);
    this.queueHealthClients.currentQueueHealthClient.unsubscribe(cancellationToken);
  }
}

/**
 * Queue Health service - retreives the operational data for Queue Health widget
 * Requests data from one api:
 * Messaging Current Queue Health
 */
class QueueHealthService extends BaseService {
  constructor() {
    super({ name: 'QueueHealthService' });
  }

  getQueueHealthSubscription(params) {
    if (!this.subscription) {
      this.queueHealthClients = createQueueHealthClients(params);
      this.subscription = new Subscription({
        name: 'QueueHealthSubscription',
        resourceName: RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH,
        handlers: {
          start: queueHealthSubscriptionStartMethod.bind(this),
          stop: queueHealthSubscriptionStopMethod.bind(this),
        },
      });
    }

    return this.subscription;
  }
}

const queueHealthService = new QueueHealthService();

export default queueHealthService;
